import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { ArticleCard } from '../components/ArticleCard';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Calendar, MapPin, Link as LinkIcon, Twitter, Github, Linkedin, Settings, Image as ImageIcon, Share2, Check } from 'lucide-react';

import { API, authFetch } from '../lib/api';

export const ProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  
  const [profile, setProfile] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({
    name: '',
    bio: '',
    picture: '',
    location: '',
    twitter: '',
    github: '',
    linkedin: '',
    website: '',
  });
  const [saving, setSaving] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const isOwnProfile = currentUser?.user_id === userId;

  useEffect(() => {
    fetchProfile();
    fetchArticles();
  }, [userId, currentUser?.user_id]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/users/${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setEditData({
          name: data.name || '',
          bio: data.bio || '',
          picture: data.picture || '',
          location: data.location || '',
          twitter: data.social_links?.twitter || '',
          github: data.social_links?.github || '',
          linkedin: data.social_links?.linkedin || '',
          website: data.social_links?.website || '',
        });
      } else {
        toast.error('Profile not found');
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
    setLoading(false);
  };

  const fetchArticles = async () => {
    try {
      const isOwn = currentUser && currentUser.user_id === userId;
      const url = `${API}/articles?author_id=${userId}&limit=50${isOwn ? '&include_hidden=true' : ''}`;
      const response = await authFetch(url);

      if (response.ok) {
        const data = await response.json();
        setArticles(data);
      }
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    }
  };

  const handleFollow = async () => {
    if (!currentUser) {
      toast.error('Please sign in to follow');
      return;
    }

    try {
      const method = profile.is_following ? 'DELETE' : 'POST';
      const response = await authFetch(`${API}/users/${userId}/follow`, {
        method,
      });

      if (response.ok) {
        setProfile(prev => ({
          ...prev,
          is_following: !prev.is_following,
          followers_count: prev.is_following ? prev.followers_count - 1 : prev.followers_count + 1
        }));
        toast.success(profile.is_following ? 'Unfollowed' : 'Following');
      }
    } catch (error) {
      toast.error('Failed to update follow status');
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const social_links = {};
      if (editData.twitter.trim()) social_links.twitter = editData.twitter.trim().replace(/^@/, '');
      if (editData.github.trim()) social_links.github = editData.github.trim().replace(/^@/, '');
      if (editData.linkedin.trim()) social_links.linkedin = editData.linkedin.trim();
      if (editData.website.trim()) {
        let url = editData.website.trim();
        if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
        social_links.website = url;
      }

      const payload = {
        name: editData.name,
        bio: editData.bio,
        picture: editData.picture,
        location: editData.location,
        social_links,
      };

      const response = await authFetch(`${API}/users/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setProfile(prev => ({ ...prev, ...updatedUser }));
        setEditDialogOpen(false);
        toast.success('Profile updated');
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    }
    setSaving(false);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setEditData(prev => ({ ...prev, picture: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleShareProfile = async () => {
    const url = `${window.location.origin}/profile/${profile.user_id}`;
    const shareText = profile.bio
      ? `${profile.name} on The Blogs Grid — ${profile.bio}`
      : `${profile.name} on The Blogs Grid`;

    // Try Web Share API on mobile/supported browsers
    if (navigator.share) {
      try {
        await navigator.share({ title: profile.name, text: shareText, url });
        return;
      } catch (err) {
        // User cancelled or share failed - fall through to clipboard
        if (err?.name === 'AbortError') return;
      }
    }

    // Fallback: copy URL to clipboard
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      toast.success('Profile link copied to clipboard');
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleBookmarkChange = (articleId, isBookmarked) => {
    setArticles(prev => prev.map(article => 
      article.article_id === articleId 
        ? { ...article, is_bookmarked: isBookmarked }
        : article
    ));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Handle Python datetime format (space separator) and ISO format
    const normalized = String(dateString).replace(' ', 'T');
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex items-start gap-6 mb-8">
            <Skeleton className="w-24 h-24 rounded-full" />
            <div className="flex-1">
              <Skeleton className="h-8 w-48 mb-2" />
              <Skeleton className="h-4 w-32 mb-4" />
              <Skeleton className="h-16 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const profileUrl = `https://theblogsgrid.com/profile/${profile.user_id}`;
  const profileDescription = profile.bio
    ? `${profile.name} on The Blogs Grid — ${profile.bio}`
    : `${profile.name}'s profile on The Blogs Grid. Read ${profile.articles_count || 0} article${profile.articles_count === 1 ? '' : 's'}.`;
  const profileJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: profile.name,
    url: profileUrl,
    description: profile.bio || undefined,
    image: profile.picture || undefined,
    sameAs: Object.values(profile.social_links || {}).filter(Boolean),
  };

  return (
    <div className="min-h-screen bg-background paper-texture" data-testid="profile-page">
      <Helmet>
        <title>{`${profile.name} — The Blogs Grid`}</title>
        <meta name="description" content={profileDescription} />
        <link rel="canonical" href={profileUrl} />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={`${profile.name} — The Blogs Grid`} />
        <meta property="og:description" content={profileDescription} />
        <meta property="og:url" content={profileUrl} />
        {profile.picture
          ? <meta property="og:image" content={profile.picture} />
          : <meta property="og:image" content="https://theblogsgrid.com/og-image.png" />}
        <meta property="profile:username" content={profile.name} />
        <meta name="twitter:card" content={profile.picture ? 'summary' : 'summary'} />
        <meta name="twitter:title" content={`${profile.name} — The Blogs Grid`} />
        <meta name="twitter:description" content={profileDescription} />
        {profile.picture && <meta name="twitter:image" content={profile.picture} />}
        <script type="application/ld+json">{JSON.stringify(profileJsonLd)}</script>
      </Helmet>
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Profile Header */}
        <div className="flex flex-col sm:flex-row items-start gap-6 mb-10 fade-up">
          <Avatar className="w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0">
            <AvatarImage src={profile.picture} alt={profile.name} />
            <AvatarFallback className="bg-accent text-accent-foreground text-2xl">
              {getInitials(profile.name)}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h1 
                className="text-3xl sm:text-4xl font-bold"
                style={{ fontFamily: 'Fraunces, serif' }}
                data-testid="profile-name"
              >
                {profile.name}
              </h1>
              
              {isOwnProfile ? (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShareProfile}
                    data-testid="share-profile-btn"
                  >
                    {shareCopied ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Share2 className="w-4 h-4 mr-2" />
                        Share
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditDialogOpen(true)}
                    data-testid="edit-profile-btn"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Edit profile
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShareProfile}
                    data-testid="share-profile-btn"
                  >
                    {shareCopied ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Share2 className="w-4 h-4 mr-2" />
                        Share
                      </>
                    )}
                  </Button>
                  <Button
                    variant={profile.is_following ? "outline" : "default"}
                    size="sm"
                    onClick={handleFollow}
                    className="rounded-full"
                    data-testid="follow-btn"
                  >
                    {profile.is_following ? 'Following' : 'Follow'}
                  </Button>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
              <span>{profile.followers_count} Followers</span>
              <span>·</span>
              <span>{profile.following_count} Following</span>
              <span>·</span>
              <span>{profile.articles_count} Articles</span>
            </div>
            
            {profile.bio && (
              <p className="text-foreground/80 mb-4" data-testid="profile-bio">
                {profile.bio}
              </p>
            )}
            
            <div className="flex items-center flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Joined {formatDate(profile.created_at)}
              </span>

              {profile.location && (
                <span className="flex items-center gap-1" data-testid="profile-location">
                  <MapPin className="w-4 h-4" />
                  {profile.location}
                </span>
              )}

              {profile.social_links?.twitter && (
                <a 
                  href={`https://twitter.com/${profile.social_links.twitter}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-accent transition-colors"
                  data-testid="profile-twitter-link"
                >
                  <Twitter className="w-4 h-4" />
                  @{profile.social_links.twitter}
                </a>
              )}

              {profile.social_links?.github && (
                <a
                  href={`https://github.com/${profile.social_links.github}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-accent transition-colors"
                  data-testid="profile-github-link"
                >
                  <Github className="w-4 h-4" />
                  {profile.social_links.github}
                </a>
              )}

              {profile.social_links?.linkedin && (
                <a
                  href={profile.social_links.linkedin.startsWith('http') ? profile.social_links.linkedin : `https://linkedin.com/in/${profile.social_links.linkedin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-accent transition-colors"
                  data-testid="profile-linkedin-link"
                >
                  <Linkedin className="w-4 h-4" />
                  LinkedIn
                </a>
              )}

              {profile.social_links?.website && (
                <a 
                  href={profile.social_links.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-accent transition-colors"
                  data-testid="profile-website-link"
                >
                  <LinkIcon className="w-4 h-4" />
                  Website
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Articles */}
        <Tabs defaultValue="articles" className="mt-8">
          <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start gap-6 h-auto pb-0">
            <TabsTrigger 
              value="articles" 
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent pb-3 px-0"
              data-testid="tab-articles"
            >
              Articles
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="articles" className="mt-6">
            <div className="divide-y divide-border">
              {articles.length > 0 ? (
                articles.map(article => (
                  <ArticleCard 
                    key={article.article_id} 
                    article={article}
                    onBookmarkChange={handleBookmarkChange}
                  />
                ))
              ) : (
                <div className="py-16 text-center">
                  <p className="text-muted-foreground">
                    {isOwnProfile 
                      ? "You haven't published any articles yet."
                      : "This author hasn't published any articles yet."}
                  </p>
                  {isOwnProfile && (
                    <Button
                      onClick={() => navigate('/write')}
                      className="mt-4 rounded-none"
                    >
                      Write your first article
                    </Button>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit Profile Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="edit-profile-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fraunces, serif' }}>Edit Profile</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Avatar */}
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16">
                <AvatarImage src={editData.picture} alt={editData.name} />
                <AvatarFallback className="bg-accent text-accent-foreground">
                  {getInitials(editData.name)}
                </AvatarFallback>
              </Avatar>
              <label className="cursor-pointer">
                <Button variant="outline" size="sm" asChild>
                  <span>
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Change photo
                  </span>
                </Button>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="profile-photo-input"
                />
              </label>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={editData.name}
                onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Your name"
                data-testid="profile-name-input"
              />
            </div>

            {/* Bio */}
            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={editData.bio}
                onChange={(e) => setEditData(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Tell us about yourself..."
                rows={3}
                maxLength={280}
                data-testid="profile-bio-input"
              />
              <p className="text-xs text-muted-foreground text-right">{editData.bio.length}/280</p>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="location" className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" /> Location
              </Label>
              <Input
                id="location"
                value={editData.location}
                onChange={(e) => setEditData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="City, Country"
                data-testid="profile-location-input"
              />
            </div>

            <div className="pt-2 border-t border-border">
              <p className="text-sm font-medium mb-3">Social links</p>

              {/* Twitter */}
              <div className="space-y-2 mb-3">
                <Label htmlFor="twitter" className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Twitter className="w-3.5 h-3.5" /> Twitter / X handle
                </Label>
                <Input
                  id="twitter"
                  value={editData.twitter}
                  onChange={(e) => setEditData(prev => ({ ...prev, twitter: e.target.value }))}
                  placeholder="username (without @)"
                  data-testid="profile-twitter-input"
                />
              </div>

              {/* GitHub */}
              <div className="space-y-2 mb-3">
                <Label htmlFor="github" className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Github className="w-3.5 h-3.5" /> GitHub username
                </Label>
                <Input
                  id="github"
                  value={editData.github}
                  onChange={(e) => setEditData(prev => ({ ...prev, github: e.target.value }))}
                  placeholder="github-username"
                  data-testid="profile-github-input"
                />
              </div>

              {/* LinkedIn */}
              <div className="space-y-2 mb-3">
                <Label htmlFor="linkedin" className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Linkedin className="w-3.5 h-3.5" /> LinkedIn
                </Label>
                <Input
                  id="linkedin"
                  value={editData.linkedin}
                  onChange={(e) => setEditData(prev => ({ ...prev, linkedin: e.target.value }))}
                  placeholder="linkedin-id or full URL"
                  data-testid="profile-linkedin-input"
                />
              </div>

              {/* Website */}
              <div className="space-y-2">
                <Label htmlFor="website" className="flex items-center gap-2 text-xs text-muted-foreground">
                  <LinkIcon className="w-3.5 h-3.5" /> Website
                </Label>
                <Input
                  id="website"
                  value={editData.website}
                  onChange={(e) => setEditData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://yourdomain.com"
                  data-testid="profile-website-input"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} data-testid="profile-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSaveProfile} disabled={saving} data-testid="profile-save-btn">
              {saving ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
